import { Activity } from '@apeiron/common'
import { useCurrencyContext } from '@apeiron/currency'
import {
  DEFAULT_ANIMATE_SHORT_DURATION,
  NextLink,
  useWeb3
} from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import ApostleActivityTypeIcon from '@src/components/apostles/activity/ApostleActivityTypeIcon'
import { SectionContent } from '@src/components/planet/detail/SectionContent'
import PriceText from '@src/components/share/generic/PriceText'
import ENV_CONFIG from '@src/config'
import { ActivityRoute, ZERO_ADDRESS } from '@src/constants/activity'
import { DATE_DISPLAY_FORMAT_MONTH_DAY_YEAR } from '@src/constants/date'
import useURL from '@src/hooks/common/router/useURL'
import { format } from 'date-fns'
import * as R from 'ramda'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import MiddleEllipsis from 'react-middle-ellipsis'

const StyledSectionContent = styled(SectionContent)`
  width: 100%;
  margin-bottom: 8px;
`

const MainText = styled(Typography)`
  font-size: 16px;
  line-height: 18px;
`

const Title = styled(MainText)`
  color: #c4c4c4;
  margin-bottom: 2px;
`

const ValueText = styled(MainText)`
  color: #ffffff;
  margin-bottom: 2px;
  text-overflow: ellipsis;
  overflow: hidden;
  transition: color ${DEFAULT_ANIMATE_SHORT_DURATION}ms;
`

const StyledNextLink = styled(NextLink)`
  flex: 1;
  min-width: 0;
  :hover {
    .Custom-Value-Text {
      color: #febe37;
    }
  }
`

const ItemContainer = styled(Box)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  white-space: nowrap;
`

const SecondaryValueText = styled(Typography)`
  color: #c4c4c4;
  font-size: 12px;
  line-height: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ContentContainer = styled(Box)`
  display: flex;
  gap: 8px;
  width: 100%;
  ${props => props.theme.breakpoints.up('res768')} {
    margin: 12px 12px 4px 20px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    margin: 8px 8px 4px 8px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    flex-direction: column;
  }
`

const ActivityTypeIcon = styled(ApostleActivityTypeIcon)`
  ${props => props.theme.breakpoints.up('res768')} {
    margin-right: 12px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    margin-right: 8px;
  }
  margin-bottom: 8px;
`

const StyledPriceText = styled(PriceText)`
  .Custom-Icon {
    width: 16px;
    height: 16px;
  }
  .Custom-Text {
    color: white;
  }
  margin-top: 15px;
`

const PriceContainer = styled(ItemContainer)`
  flex: 0.5;
  margin-bottom: 8px;
`

const getFormatDateString = (date: Date): string =>
  format(date, DATE_DISPLAY_FORMAT_MONTH_DAY_YEAR)

export const SaleHistoryItem: React.FC<SaleHistoryItemProps> = props => {
  const { route, saleHistory } = props

  const { t } = useTranslation()

  const { fromWei } = useWeb3()

  const { buildURL } = useURL()

  const { getCurrency, getApeironPrice } = useCurrencyContext()

  const currency = getCurrency(saleHistory.currencyType)

  const hasBuyerAccount = !R.isEmpty(saleHistory?.account.to?.name)

  const buyerName =
    !!saleHistory?.account.to?.walletAddress &&
    saleHistory.account.to?.walletAddress === ZERO_ADDRESS
      ? t('common.foonie_magus')
      : saleHistory.account.to?.name

  const defaultSellerName = t('common.foonie_magus')

  const hasSellerAccount = !R.isEmpty(saleHistory?.account.from?.name)

  const sellerName =
    !!saleHistory?.account.from?.walletAddress &&
    saleHistory.account.from?.walletAddress === ZERO_ADDRESS
      ? defaultSellerName
      : saleHistory.account.from?.name

  const buyerAddress = saleHistory?.account.to?.walletAddress
    ? saleHistory?.account.to?.walletAddress
    : 'N/A'

  const sellerAddress = saleHistory?.account.from?.walletAddress
    ? saleHistory?.account.from?.walletAddress
    : 'N/A'

  const price =
    saleHistory.price && currency
      ? fromWei(saleHistory.price, currency.decimals)
      : '0'

  const formattedPrice = getApeironPrice(
    saleHistory.currencyType,
    saleHistory.price
  )

  const disablePrice = sellerName === defaultSellerName && price === '0'

  const buyerUrl = useMemo(() => {
    return buildURL(ENV_CONFIG.WEB_URL.MARKETPLACE, String(route), {
      ownerWalletAddresses: buyerAddress
    })
  }, [buildURL, buyerAddress, route])

  const sellerUrl = useMemo(() => {
    return buildURL(ENV_CONFIG.WEB_URL.MARKETPLACE, String(route), {
      ownerWalletAddresses: sellerAddress
    })
  }, [buildURL, route, sellerAddress])

  return (
    <StyledSectionContent>
      <ContentContainer>
        <ActivityTypeIcon activity={saleHistory} />
        <StyledNextLink href={hasBuyerAccount ? buyerUrl : ''}>
          <ItemContainer>
            <Title>{t('common.buyer')}</Title>
            <ValueText className='Custom-Value-Text'>{buyerName}</ValueText>
            <MiddleEllipsis>
              <SecondaryValueText>{`(${buyerAddress})`}</SecondaryValueText>
            </MiddleEllipsis>
          </ItemContainer>
        </StyledNextLink>
        <StyledNextLink href={hasSellerAccount ? sellerUrl : ''}>
          <ItemContainer>
            <Title>{t('common.seller')}</Title>
            <ValueText>{sellerName}</ValueText>
            <MiddleEllipsis>
              <SecondaryValueText>{`(${sellerAddress})`}</SecondaryValueText>
            </MiddleEllipsis>
          </ItemContainer>
        </StyledNextLink>
        <PriceContainer>
          <StyledPriceText
            disabled={disablePrice}
            icon={currency?.imageLite}
            displayPrice={formattedPrice}
          />
          <SecondaryValueText>
            {getFormatDateString(new Date(saleHistory.transaction.commitAt))}
          </SecondaryValueText>
        </PriceContainer>
      </ContentContainer>
    </StyledSectionContent>
  )
}

export type SaleHistoryItemProps = {
  route: ActivityRoute
  saleHistory: Activity
}
